import type { IntentCandidate, LocalAiRuntime, VoiceManifest } from '../types/LocalAiTypes';
import { normalizeTranscript } from '../normalize/TranscriptNormalizer';
import { recordLocalAiDuration } from '../models/ModelTelemetry';
import { workerModelConfig } from '../models/WorkerModelConfig';
import { WorkerClient } from '../workers/WorkerClient';
import { contextBoost, contextMatches } from './ContextMatcher';

interface CorpusItem { intentIndex: number; text: string }

export class SemanticMatcher {
  private readonly client: WorkerClient;
  private initialization?: Promise<void>;
  private corpus?: readonly CorpusItem[];
  private embeddings?: readonly number[][];

  public constructor(
    private readonly manifest: VoiceManifest,
    private readonly runtime: LocalAiRuntime,
    private readonly halfPrecision: boolean,
  ) {
    this.client = new WorkerClient(new Worker(new URL('../workers/embedding.worker.ts', import.meta.url), { type: 'module' }));
  }

  public async candidates(value: string, route: string): Promise<IntentCandidate[]> {
    const started = performance.now();
    try {
      await this.initialize();
      const query = await this.client.request<number[][]>({ type: 'EMBED', texts: [`query: ${value}`] });
      const vector = query[0];
      if (!vector || !this.corpus || !this.embeddings) return [];
      const best = new Map<number, number>();
      this.embeddings.forEach((embedding, index) => {
        const corpus = this.corpus?.[index];
        if (!corpus) return;
        best.set(corpus.intentIndex, Math.max(best.get(corpus.intentIndex) ?? -1, dot(vector, embedding)));
      });
      return [...best.entries()].flatMap(([intentIndex, confidence]) => {
        const intent = this.manifest.intents[intentIndex];
        if (!intent || !contextMatches(intent, route)) return [];
        return [{
          intent,
          confidence: Math.max(0, Math.min(1, confidence + contextBoost(intent, route))),
          source: 'semantic' as const,
          parameters: {},
        }];
      }).sort((left, right) => right.confidence - left.confidence);
    } finally {
      recordLocalAiDuration('semantic', performance.now() - started);
    }
  }

  public dispose(): void {
    this.client.dispose();
  }

  private initialize(): Promise<void> {
    if (this.initialization) return this.initialization;
    this.initialization = (async () => {
      await this.client.request<void>({
        type: 'INIT',
        config: workerModelConfig(this.manifest, 'intent.semantic', this.runtime, this.halfPrecision),
      }, { timeoutMs: 180_000 });
      this.corpus = this.manifest.intents.flatMap((intent, intentIndex) => [
        ...intent.examples.fa,
        ...intent.examples.en,
        ...intent.examples.ar,
        ...intent.aliases.fa,
        ...intent.aliases.en,
        ...intent.aliases.ar,
      ].map((text) => ({ intentIndex, text: normalizeTranscript(text).normalizedText })));
      this.embeddings = await this.client.request<number[][]>({
        type: 'EMBED',
        texts: this.corpus.map((item) => `passage: ${item.text}`),
      }, { timeoutMs: 180_000 });
    })();
    return this.initialization;
  }
}

function dot(left: readonly number[], right: readonly number[]): number {
  let sum = 0;
  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    sum += (left[index] ?? 0) * (right[index] ?? 0);
  }
  return sum;
}
